import {useState} from 'react';
import {I18n} from 'aws-amplify/utils';
import {
    Alert,
    Button,
    Card,
    Divider,
    Group,
    Stack,
    Text,
} from '@mantine/core';

import {ProviderConnection} from '../types';
import {
    presentStatus,
    revalidateLabel,
    statusColor,
} from '../statusPresentation';
import {ConnectionStatusBadge} from './ConnectionStatusBadge';
import {CapabilitySnapshot} from './CapabilitySnapshot';
import {ExternalIdPanel} from './ExternalIdPanel';
import {RevalidateDrawer} from '../drawers/RevalidateDrawer';
import {DisconnectProviderModal} from './DisconnectProviderModal';
import {useProviderDisconnect} from '../hooks/useProviderDisconnect';

// FNP-1248 — THE DETAIL VIEW.
//
// Four things, in the order a tenant needs them:
//   1. STATUS, with the provider's `statusDetail` verbatim.
//   2. THE EXTERNAL ID, when this is an ASSUMED_ROLE connection.
//   3. THE CAPABILITY SNAPSHOT, read-only rendered as read-only.
//   4. THE ACTIONS — re-validate, and disconnect behind its preview.

export const Profile = (
    {
        connection,
        onDisconnected,
    }: {
        connection: ProviderConnection,
        // Called after a confirmed disconnect, so the page can leave — the
        // record this view is built on no longer exists.
        onDisconnected: () => void,
    },
) => {

    const [revalidateOpened, setRevalidateOpened] = useState(false);

    const presentation = presentStatus(connection);

    const {
        connection: disconnectTarget,
        opened: disconnectOpened,
        requestDisconnect,
        cancelDisconnect,
        confirmDisconnect,
        preview,
        isPreviewLoading,
        isPreviewError,
        retryPreview,
        isDisconnecting,
        canConfirm,
    } = useProviderDisconnect();

    const handleConfirmDisconnect = async () => {
        await confirmDisconnect();
        onDisconnected();
    };

    return (
        <Stack gap={'lg'} p={'md'}>

            {/* ─── 1. STATUS ─── */}
            <Card withBorder={true} padding={'lg'}>
                <Stack gap={'md'}>

                    <Group justify={'space-between'} align={'flex-start'}>
                        <Stack gap={4}>
                            <Text fw={600}>{connection.displayName}</Text>
                            <Text size={'sm'} c={'dimmed'}>
                                {I18n.get('Provider')}: {connection.providerId}
                            </Text>
                        </Stack>
                        <ConnectionStatusBadge connection={connection}/>
                    </Group>

                    <Alert
                        color={statusColor(presentation.tone)}
                        variant={'light'}
                        title={presentation.label}
                    >
                        <Stack gap={'xs'}>
                            <Text size={'sm'}>{presentation.summary}</Text>

                            {/* ⚠️ THE PROVIDER'S OWN WORDS, VERBATIM AND
                                UNTRUNCATED. Never re-worded on the way to the
                                screen — classify for the core, preserve for the
                                human (FNP-1180). FNP-1087 took a whole
                                investigation because a normalised class was all
                                anyone had. */}
                            {connection.statusDetail && (
                                <Stack gap={2}>
                                    <Text size={'xs'} c={'dimmed'} fw={500}>
                                        {I18n.get('Provider response')}
                                    </Text>
                                    <Text size={'xs'} c={'dimmed'} data-testid={'status-detail'}>
                                        {connection.statusDetail}
                                    </Text>
                                </Stack>
                            )}
                        </Stack>
                    </Alert>

                </Stack>
            </Card>

            {/* ─── 2. THE EXTERNAL ID ───
                Renders itself to null for an API_TOKEN connection, which has
                none. No role ARN is passed: it is not stored on the connection
                record, so on a page load there is genuinely none to show and
                inventing one would be worse than omitting the line. */}
            <ExternalIdPanel connection={connection}/>

            {/* ─── 3. CAPABILITIES ─── */}
            <Card withBorder={true} padding={'lg'}>
                <CapabilitySnapshot connection={connection}/>
            </Card>

            {/* ─── 4. ACTIONS ─── */}
            <Card withBorder={true} padding={'lg'}>
                <Stack gap={'md'}>

                    <Stack gap={4}>
                        <Text fw={500} size={'sm'}>{I18n.get('Re-validate')}</Text>
                        <Text size={'sm'} c={'dimmed'}>
                            {connection.credentialType === 'ASSUMED_ROLE'
                                ? I18n.get('Once the external ID is in your role\'s trust policy, re-validate to check the connection. Your external ID does not change.')
                                : I18n.get('Supply a fresh API token to replace the stored one.')}
                        </Text>
                    </Stack>

                    <Group>
                        <Button
                            variant={'outline'}
                            onClick={() => setRevalidateOpened(true)}
                        >
                            {/* UNREACHABLE says "Retry"; INVALID says
                                "Re-validate", because clicking again unchanged
                                cannot fix a rejection. */}
                            {revalidateLabel(connection.status)}
                        </Button>
                    </Group>

                    <Divider my={'xs'}/>

                    <Stack gap={4}>
                        <Text fw={500} size={'sm'}>{I18n.get('Disconnect')}</Text>
                        <Text size={'sm'} c={'dimmed'}>
                            {I18n.get('Removes this connection and deletes its stored credential. Flexi will show you what this affects before anything is removed.')}
                        </Text>
                    </Stack>

                    <Group>
                        <Button
                            color={'red'}
                            variant={'outline'}
                            onClick={() => requestDisconnect(connection)}
                        >
                            {I18n.get('Disconnect provider')}
                        </Button>
                    </Group>

                </Stack>
            </Card>

            {revalidateOpened && (
                <RevalidateDrawer
                    connection={connection}
                    opened={revalidateOpened}
                    onClose={() => setRevalidateOpened(false)}
                />
            )}

            <DisconnectProviderModal
                connection={disconnectTarget}
                preview={preview}
                opened={disconnectOpened}
                onClose={cancelDisconnect}
                onConfirm={handleConfirmDisconnect}
                canConfirm={canConfirm}
                isPreviewLoading={isPreviewLoading}
                isPreviewError={isPreviewError}
                onRetryPreview={retryPreview}
                isDisconnecting={isDisconnecting}
            />

        </Stack>
    );
};
